import { addError, addLoading, getError, getLoading, isSupersededRequest, removeLoadingAndError } from '../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../core/redux/api/circuitBreaker/module';
import { apiGetResortMapping } from '../core/redux/api/endpoints';
import { IAppState } from '../core/redux/IAppState';
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
} from '../core/redux/api/reduxStandardApiAction';
import { ACTION_TYPE } from '../core/redux/actionType';
import { assoc, isNilOrEmpty } from '../language/module';
import { ResortMappingObject, RTPMappingProduct, SiriuswareMappingProduct} from './productMappings';
import { getResource } from '../language/resources/getResource';
import { v4 as uuidv4 } from 'uuid';
import { getProductHydrationErrors } from './hydrationErrors';
import * as R from 'ramda';
import { ApiError } from '../core/redux/api/apiError';

const KEY_ACTION = ACTION_TYPE.RESORT_MAPPING_REQUEST;
export const ACTIONS    = [
  ACTION_TYPE.RESORT_MAPPING_REQUEST,
  ACTION_TYPE.RESORT_MAPPING_RESPONSE,
  ACTION_TYPE.RESORT_MAPPING_ERROR,
];


/**
 * Whether a mapping load is in flight for the given resort. This request is keyed by resort abbreviation,
 * not by action type, so read it through here rather than calling getLoading at the component.
 */
export const isResortMappingLoading = (state: IAppState, resortAbbreviation: string): boolean =>
  !!getLoading(state, resortAbbreviation, KEY_ACTION);


/** Whether this load has landed for the given resort since the last switch, rows or not. */
export const haveResortMappingsLoaded = (state: IAppState, resortAbbreviation: string): boolean =>
  state.mappingsLoadedFor === resortAbbreviation;


/** The given resort's last mapping load failure. Kept until the next request or a switch. */
export const getResortMappingLoadError = (state: IAppState, resortAbbreviation: string): ApiError | undefined =>
  getError(state, resortAbbreviation, KEY_ACTION);


/** Whether the breaker is holding this request open, so a dispatch would bail out without firing. */
export const isResortMappingSuppressed = (state: IAppState): boolean =>
  hasTrippedBreaker(state, KEY_ACTION);


/**
 * Makes an action that can be dispatched to get resort product mappings (excluding mapping errors)
 */
export const getResortMappingAction = (
  includeOld: boolean,
  resortAbbreviation: string,
  resortConfigurable: boolean,
) => {
  const requestId = uuidv4();

  return makeApiAction(
  {
    method:   'GET',
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    endpoint: apiGetResortMapping(includeOld, resortAbbreviation, resortConfigurable),
    types:     [
      {
        type: ACTIONS[0],
        meta: { requestId, resortAbbreviation },
      },
      { 
        type: ACTIONS[1],
        meta: { requestId, resortAbbreviation },
      },
      {
        type: ACTIONS[2],
        meta: { requestId, resortAbbreviation },
      }
    ],
    // Bail if already loading results for that query for that resort
    bailout:  (state: IAppState) => isResortMappingSuppressed(state) || isResortMappingLoading(state, resortAbbreviation),
  },
);
}


const reducer: ApiReducer<ResortMappingObject> = (
    state,
    action,
    error,
    isLoading,
    payload
): IAppState => {
  const resort         = action.meta.resortAbbreviation;
  const appStateResort = R.path<string>(['selectedResort', 'resortAbbreviation'], state);

  // A switch prefetches this, so a response for the resort switched away from can land while
  // mappingRequestId still names it, writing its rows under the new resort
  if (action.type === ACTION_TYPE.RESORT_MAPPING_RESPONSE && !isNilOrEmpty(appStateResort) && appStateResort !== resort) {
    const withBreaker = updateBreaker(state, KEY_ACTION, !!error);
    return removeLoadingAndError(withBreaker, resort, KEY_ACTION);
  }

  if  (action.type === KEY_ACTION) {
    const newState = assoc('mappingRequestId', action.meta.requestId, state);
    // Names its owner, so an earlier request landing late cannot settle it
    return addLoading(newState, resort, KEY_ACTION, { requestId: action.meta.requestId });
  }

  if (isLoading) {
    return addLoading(state, resort, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  // Superseded: a later request for this resort owns its loading entry, error and rows
  if (isSupersededRequest(state, resort, KEY_ACTION, action.meta.requestId, state.mappingRequestId)) {
    return withBreaker;
  }

  if (error) {
    return addError(
      withBreaker,
      resort,
      KEY_ACTION,
      getResource(state.locale, 'GET_RESORT_MAPPING_ACTION_ERROR'),
    );
  }

  // Remove loading/error since we're not loading or erroring
  const noLoadingOrError = removeLoadingAndError(withBreaker, resort, KEY_ACTION);

  // Stamped with no rows too, so the page reads that as no data rather than as still loading
  const loaded = assoc('mappingsLoadedFor', resort, noLoadingOrError);

  if (!payload?.productMappings) {
    return loaded;
  }

  const resortMappingObject = payload;
    /**
     * Typescript still doesn't support .filter on < T[] | O[] > so for now we need to type assert based on POS Provider
     */
  if (resortMappingObject.posProvider === 'RTP') {
    const rtpMappings = resortMappingObject.productMappings as RTPMappingProduct[];
    const filteredMappings = rtpMappings.filter(mapping => mapping.isResortConfigurable);
    // Retain previous value for error status and error detail if set as these are not loaded by this action
    const mappingsWithErrors = updateRTPErrorFields(filteredMappings, state.resortMappingHistories.productMappings as RTPMappingProduct[]);
    const mappingsWithHydrationErrors = mappingsWithErrors.map((mapping) => {
      mapping.hydrationErrors = getProductHydrationErrors(state.productHydrationErrors, mapping.entitlementTypeId, mapping.fulfillmentSeasonId, mapping.ageBin);
      return mapping;
    });

    return assoc(
      'resortMappingHistories',
      {
        posProvider: payload.posProvider,
        productMappings: mappingsWithHydrationErrors,
        hasError: payload.hasError
      },
      loaded
    );
  } else {
    const siriuswareMappings = resortMappingObject.productMappings as SiriuswareMappingProduct[];
    const filteredMappings = siriuswareMappings.filter(mapping => mapping.isResortConfigurable);
    // Retain previous value for error status and error detail if set as these are not loaded by this action
    const mappingsWithErrors = updateSWErrorFields(filteredMappings, state.resortMappingHistories.productMappings as SiriuswareMappingProduct[]);
    const mappingsWithHydrationErrors = mappingsWithErrors.map((mapping) => {
      mapping.hydrationErrors = getProductHydrationErrors(state.productHydrationErrors, mapping.entitlementTypeId, mapping.fulfillmentSeasonId, mapping.ageBin);
      return mapping;
    });

    return assoc(
      'resortMappingHistories',
      {
        posProvider: payload.posProvider,
        productMappings: mappingsWithHydrationErrors,
        hasError: payload.hasError
      },
      loaded
    );
  }

};

const updateRTPErrorFields = (updatedMappings : RTPMappingProduct[], currentErrorMappings : RTPMappingProduct[]) => {
    const updateFields = updatedMappings.map((mapping) => {
      const currentErrorState = currentErrorMappings.find((item) => { return item.id === mapping.id; });
      mapping.isError = currentErrorState?.isError ?? null;
      mapping.errorDescription = currentErrorState?.errorDescription ?? '';
      return mapping;
    });
    return updateFields;
  };

  const updateSWErrorFields = (updatedSWMappings : SiriuswareMappingProduct[], currentSWErrorMappings : SiriuswareMappingProduct[]) => {
    const updateFields = updatedSWMappings.map((mapping) => {
      const currentErrorState = currentSWErrorMappings.find((item) => { return item.id === mapping.id; });
      mapping.isError = currentErrorState?.isError ?? null;
      mapping.errorDescription = currentErrorState?.errorDescription ?? '';
      return mapping;
    });
    return updateFields;
  };


/**
 * Function that maps actions into new app state. Specifically, updates app state with information from actions related
 * to a guest's redemptions at resorts while using a specific pass. Returns a new app state, of course.
 */
export const getResortMappingReducer = makeApiReducer<ResortMappingObject>(ACTIONS, KEY_ACTION, reducer);
